import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Sparkles } from 'lucide-react'
import { Button, ButtonVariant, ButtonStyle, H2, Body } from '@meiuca/ui-components'
import { SportModalitySelector } from './SportModalitySelector'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents'
import type { SportModality } from '@/types/address'

interface UserPreferencesModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (modalities: SportModality[]) => Promise<void>
  onSkip?: () => void
  title?: string
  description?: string
  allowSkip?: boolean
}

export function UserPreferencesModal({
  isOpen,
  onClose,
  onSave,
  onSkip,
  title = 'Quais esportes você pratica?',
  description = 'Conte pra gente para vermos as melhores arenas para você',
  allowSkip = true
}: UserPreferencesModalProps) {
  const [selectedModalities, setSelectedModalities] = useState<SportModality[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (selectedModalities.length === 0) {
      setError('Selecione pelo menos uma modalidade')
      return
    }

    try {
      setIsSaving(true)
      setError(null)

      analyticsService.track(ANALYTICS_EVENTS.PREFERENCES_SAVED, {
        modalities_count: selectedModalities.length,
        modalities: selectedModalities.map(m => m.id).join(','),
        source: 'preferences_modal'
      })

      await onSave(selectedModalities)
      onClose()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar preferências'
      setError(errorMessage)

      analyticsService.track(ANALYTICS_EVENTS.PREFERENCES_SAVE_ERROR, {
        error_message: errorMessage,
        modalities_count: selectedModalities.length
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSkip = () => {
    analyticsService.track(ANALYTICS_EVENTS.PREFERENCES_MODAL_SKIPPED, {
      source: 'preferences_modal'
    })

    if (onSkip) {
      onSkip()
    } else {
      onClose()
    }
  }

  const handleClose = () => {
    analyticsService.track(ANALYTICS_EVENTS.PREFERENCES_MODAL_CLOSED, {
      modalities_count: selectedModalities.length,
      source: 'close_button'
    })
    onClose()
  }

  // Track modal view when opened
  if (isOpen) {
    analyticsService.track(ANALYTICS_EVENTS.PREFERENCES_MODAL_VIEWED, {
      source: 'preferences_modal'
    })
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-2xl bg-background-white rounded-2xl shadow-2xl pointer-events-auto max-h-[90vh] flex flex-col"
            >
              {/* Header */}
              <div className="relative p-6 border-b border-gray-200">
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Fechar"
                >
                  <X className="w-5 h-5 text-text-secondary" />
                </button>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-brand-primary/10 rounded-xl">
                    <Sparkles className="w-6 h-6 text-brand-primary" />
                  </div>
                  <div className="flex-1">
                    <H2 className="text-text-primary mb-2">{title}</H2>
                    <Body className="text-text-secondary">
                      {description}
                    </Body>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <SportModalitySelector
                  selectedModalities={selectedModalities}
                  onModalitiesChange={setSelectedModalities}
                  error={error || undefined}
                />
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-3">
                  {allowSkip && (
                    <Button
                      onClick={handleSkip}
                      buttonStyle={ButtonStyle.CLEAR}
                      disabled={isSaving}
                      className="flex-1"
                    >
                      Pular por agora
                    </Button>
                  )}
                  <Button
                    onClick={handleSave}
                    variant={ButtonVariant.PRIMARY}
                    loading={isSaving}
                    disabled={selectedModalities.length === 0}
                    className="flex-1"
                  >
                    Salvar preferências
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
